/**
 * The Terms of Use and the Privacy Policy.
 *
 * Written to describe what this product actually does with what people give it — which Workers
 * keep what, for how long, and who else sees it — rather than as boilerplate, because the account
 * screen asks people to agree to exactly this text and records which version they agreed to
 * (`TERMS_VERSION`). A change here that a person should be asked about again bumps that version.
 *
 * Plain pages, readable without an account and without JavaScript-heavy chrome: they are linked
 * from the sign-in, opened in a new tab, and read by people deciding whether to trust the product.
 */
import { Link } from 'react-router';

import { SUPPORT_EMAIL, TERMS_VERSION, SHARING_RULES } from '@orbit-mind/shared-types';

import { IconOrbit } from '../ui/icons';

/** Where people write to about their data or these terms. */
export const CONTACT_EMAIL = SUPPORT_EMAIL;

function LegalLayout({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div className="bg-canvas text-ink min-h-dvh">
      <header className="border-edge bg-surface/85 sticky top-0 border-b backdrop-blur-md">
        <div className="mx-auto flex max-w-3xl items-center gap-3 px-5 py-3">
          <Link
            to="/"
            className="text-accent flex items-center gap-2 text-xs font-semibold tracking-wide"
          >
            <IconOrbit size={28} />
            ORBIT MIND
          </Link>
        </div>
      </header>
      <main className="mx-auto max-w-3xl px-5 py-10">
        <h1 className="text-2xl font-semibold">{title}</h1>
        <p className="text-ink-muted mt-1 text-sm">Version {TERMS_VERSION}</p>
        <div className="legal-text mt-8 grid gap-6 text-[0.9375rem] leading-relaxed">
          {children}
        </div>
      </main>
    </div>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <section className="grid gap-2">
      <h2 className="text-lg font-semibold">{title}</h2>
      {children}
    </section>
  );
}

const email = (
  <a href={`mailto:${CONTACT_EMAIL}`} className="text-accent underline">
    {CONTACT_EMAIL}
  </a>
);

export function TermsPage(): React.JSX.Element {
  return (
    <LegalLayout title="Terms of Use">
      <p>
        These terms are an agreement between you and CyBrainX, the maker of Orbit Mind (“we”). By
        creating an account or using Orbit Mind you agree to them and to the{' '}
        <Link to="/privacy" className="text-accent underline">
          Privacy Policy
        </Link>
        .
      </p>

      <Section title="The service">
        <p>
          Orbit Mind is an application for making mind maps, on the web and on Android. Maps are
          kept on your device and work without an account. With an account, the maps you open are
          also kept on our servers so they reach your other devices, and you can draft maps with AI
          and share read-only links.
        </p>
      </Section>

      <Section title="Your account">
        <p>
          You sign in through a provider such as Google or with a verified email and password when
          available. You are responsible for what happens under your account and for keeping access
          to your provider account secure. You must be at least 13 years old, or the minimum age
          required where you live, to create an account.
        </p>
      </Section>

      <Section title="Your content">
        <p>
          What you put in your maps is yours. You give us only the permission we need to store it,
          copy it between your devices, back it up and show it to the people you choose to share it
          with. We do not claim ownership of it and we do not use it to advertise to you.
        </p>
        <p>
          A read-only link lets anyone who has it see that map until you turn sharing off. Share
          links only with people you trust with the map.
        </p>
      </Section>

      <Section title="Acceptable use">
        <p>
          Do not use Orbit Mind to break the law, to infringe other people’s rights, to distribute
          malware, or to disrupt the service — for example by automated sign-ins or by getting
          around its limits. We may suspend accounts that do.
        </p>
      </Section>

      <Section title="Rules for shared maps: no nudity or violence">
        <p>{SHARING_RULES.en}</p>
        <p>
          These rules apply to map text, images, drawings, notes, links and other material made
          available through Orbit Mind sharing. Do not share nudity or sexual material, depictions
          of violence, threats or instructions that encourage harming people, self-harm, cruelty,
          harassment, hate, exploitation of children, illegal material, fraud, or another person's
          private information without permission. Accept these rules before creating a sharing link.
        </p>
        <p>
          Readers can report a map, block it or block its author from the shared-map screen. Reports
          are reviewed by our support team. We may remove access to a reported map or suspend an
          author's ability to share maps. Private editing and personal synchronisation are not
          removed by a sharing suspension. A report does not automatically prove a violation.
        </p>
        <p>
          We prioritise reports of nudity, violence, threats and child exploitation. For an appeal,
          contact {email} with the report reference or affected sharing link. We review appeals and
          may restore sharing when the violation has been corrected or the decision was mistaken.
          Sharing links do not restrict access to invited friends: anyone holding a live link may
          open it. No chat, comments or direct messaging are provided.
        </p>
      </Section>

      <Section title="AI drafts">
        <p>
          Drafts made with AI are suggestions produced by a third-party model and may be wrong or
          incomplete. Nothing is added to your library until you accept it; check what you accept.
          AI use has daily limits, which may change.
        </p>
      </Section>

      <Section title="Availability and changes">
        <p>
          Orbit Mind is under active development. We work to keep it available and your maps safe,
          but we cannot promise it will be uninterrupted or free of errors. Features may change. If
          we introduce paid plans, we will say so before anything is charged.
        </p>
      </Section>

      <Section title="Ending">
        <p>
          You can stop using Orbit Mind at any time and ask us to delete your account at {email}. We
          may end access for accounts that break these terms, and will give you a chance to export
          your maps where the law allows.
        </p>
      </Section>

      <Section title="Liability">
        <p>
          To the extent the law allows, Orbit Mind is provided “as is”, and we are not liable for
          indirect damages or for loss of data you did not keep a copy of. Nothing here limits
          rights you have under the Brazilian Consumer Defense Code or other laws that cannot be
          waived.
        </p>
      </Section>

      <Section title="Law and contact">
        <p>
          These terms are governed by the laws of Brazil. If we change them in a way that matters,
          we will ask you to agree again. Questions: {email}.
        </p>
      </Section>
    </LegalLayout>
  );
}

export function PrivacyPage(): React.JSX.Element {
  return (
    <LegalLayout title="Privacy Policy">
      <p>
        This policy explains what Orbit Mind, made by CyBrainX (“we”), collects, why, where it is
        kept and for how long, and the rights you have under Brazil’s General Data Protection Law
        (LGPD) and similar laws.
      </p>

      <Section title="Reports and sharing safety">
        <p>
          When you report a shared map, we keep the affected map and author identifiers, sharing
          token, map title and revision, reason, optional explanation and submission time. We do not
          reveal the reporter's identity to the author. Authorised support staff can review reports
          and the current map; the map may have changed since the report was submitted.
        </p>
        <p>
          For signed-in readers, blocks belong to the account and follow it across devices. Guests
          use a random identifier stored in their browser or device secure storage; we keep only a
          hash on the server. It is used for reports, block lists and abuse limits, not advertising.
          Clearing browser data or changing devices can reset guest blocks. Manage or remove blocks
          from the shared-map screen or settings.
        </p>
        <p>
          Resolved reports and their explanations are normally removed after 90 days. Active
          suspension decisions are retained while sharing remains suspended so they can be reviewed
          or appealed. Outstanding reports are retained until reviewed. Do not include unnecessary
          personal information in a report. These data are used to protect the service and handle
          complaints, and are not sent to an AI moderation provider.
        </p>
      </Section>
      <Section title="What we collect, and why">
        <ul className="grid list-disc gap-2 pl-5">
          <li>
            <strong>Your account:</strong> your name and verified email address from the provider
            you sign in with, the provider’s id for your account, and when you agreed to these
            terms. We use them to sign you in and keep your maps yours.
          </li>
          <li>
            <strong>Email and password:</strong> if you choose this method, Firebase Authentication
            (Google) verifies your password and sends confirmation and recovery links. Orbit Mind
            does not store your readable password. We retain an encrypted Firebase refresh credential
            for your Orbit Mind session and periodically check that it remains valid. Pending registration
            records expire after 30 days and are removed by a bounded
            scheduled cleanup. Sessions using Firebase credentials are revalidated within five minutes;
            revoked credentials cannot renew them. Account deletion also removes the Firebase identity.
            Temporary attempt counters contain hashed keys, not readable email
            addresses.
          </li>
          <li>
            <strong>Maps you sync:</strong> the content of the maps you open while signed in, up to
            50 earlier versions of each so you can restore them, and files you attach. We use them
            only to keep your maps in step between your devices.
          </li>
          <li>
            <strong>Share links:</strong> which maps you share and when you stop.
          </li>
          <li>
            <strong>AI drafts:</strong> what you ask for is sent to our AI provider to produce the
            draft. Only the completed draft is kept privately in your account for up to 24 hours so
            you can recover a lost response without a new charge. They are unavailable after that
            deadline and removed by the scheduled cleanup. Usage metadata (length, model and cost)
            remains to enforce limits. The original request is not saved separately. Account
            deletion removes these results.
          </li>
          <li>
            <strong>Product emails:</strong> only if you turn them on. You can turn them off at any
            time.
          </li>
          <li>
            <strong>Operations:</strong> when something fails we record the kind of failure and when
            it happened — never who you are or what you were writing.
          </li>
          <li>
            <strong>Usage counts on the web:</strong> only if you allow them in the banner, and
            without identifying you.
          </li>
        </ul>
        <p>
          We do not sell your data. The Android app can show clearly labelled, non-personalized
          Google AdMob native ads alongside curated map-building guidance while AI is working. Ads
          are disabled for confirmed paid subscribers, users under 18, and users who have not stated
          their age group. An ad never grants AI credits or delays the result. Your maps,
          attachments, AI requests and account details are not supplied to the advertising SDK.
        </p>
        <p>
          Before advertising starts, the app requests applicable privacy choices through Google's
          consent platform. You can review advertising choices and your age group in Settings →
          Privacy and data. Your age-group choice is kept on your device, without your date of
          birth. The advertising SDK may process IP-derived approximate location, device or other
          identifiers, ad interactions and diagnostics to deliver ads, measure their performance and
          prevent fraud. Non-personalized advertising does not mean that no data is processed.
        </p>
      </Section>

      <Section title="Who else processes it">
        <ul className="grid list-disc gap-2 pl-5">
          <li>
            <strong>Cloudflare</strong> hosts the service, the database and the file storage.
          </li>
          <li>
            <strong>Google</strong> confirms who you are when you sign in with Google. Firebase
            Authentication manages email/password credentials and verification and recovery emails.
          </li>
          <li>
            <strong>OpenRouter</strong> and the model provider it routes to receive the text of an
            AI request in order to answer it.
          </li>
          <li>
            <strong>Google AdMob</strong> processes advertising data when advertising is enabled in
            the Android app. See{' '}
            <a href="https://policies.google.com/privacy" target="_blank" rel="noreferrer">
              Google's Privacy Policy
            </a>
            .
          </li>
          <li>
            <strong>Linkup</strong> receives search queries when an AI request uses web sources. Our
            service uses the country inferred from the request's IP address to select the
            appropriate processing region.
          </li>
        </ul>
        <p>
          These providers may process data outside Brazil, under safeguards required by the LGPD.
        </p>
      </Section>

      <Section title="How long we keep it">
        <ul className="grid list-disc gap-2 pl-5">
          <li>Your account and synced maps: while your account exists.</li>
          <li>A deleted map: 30 days in the trash, then it is removed for good.</li>
          <li>Earlier versions of a map: the latest 50.</li>
          <li>Sign-in sessions: until they expire or you sign out.</li>
          <li>Failure records: 30 days.</li>
        </ul>
      </Section>

      <Section title="Your rights">
        <p>
          You can ask us to confirm what we hold about you, to correct it, to give you a copy, to
          delete it, and to withdraw any consent you gave. Maps can be exported from the app at any
          time. Write to {email} and we will answer within the time the law sets.
        </p>
      </Section>

      <Section title="Cookies">
        <p>
          On this website, we use cookies to keep you signed in and to protect sign-in from forgery;
          we do not display AdMob ads here. Advertising in the Android app uses SDK storage and
          identifiers according to the applicable privacy choices described above.
        </p>
      </Section>

      <Section title="Security and children">
        <p>
          Data travels encrypted and access to it is restricted. Orbit Mind is not directed at
          children under 13.
        </p>
      </Section>

      <Section title="Changes and contact">
        <p>
          If this policy changes in a way that matters, we will ask you to agree again. Questions or
          requests: {email}.
        </p>
      </Section>
    </LegalLayout>
  );
}
